import React, { useCallback, useMemo } from 'react';
import { TextButton } from '../../../../../../components/Button';
import {
  defaultPermissionTooltipOption,
  ITooltipOption,
} from '../../../../../../components/KubitTooltip/globalTooltips';
import { useHighlightingContext } from '../../../../../../components/HighlightingContext';
import Popover from '../../../../../../components/Popover';
import useDashboardSelector from '../../../../../../redux/selectorHooks/useDashboardSelector';
import {
  defaultLogicOp,
  IPredicateContext,
} from '../../../../../../components/FilterWidget/types';
import {
  FieldContext,
  IFilterFieldsFragment,
  PermissionKey,
} from '../../../../../../utils/apollo/graphqlSchema';
import { defaultEventInput } from '../../../../../../utils/builder';
import { useAppDispatch } from '../../../../../../redux/hooks';
import { dashboardCustomizationSetFilter } from '../../../../../../redux/slices/dashboards/dashboardInstance';
import { MAX_DASHBOARD_FILTER_CONDITIONS } from '../../../../../../config/constants';
import useDashboardConfigSelector from '../../../../../../redux/selectorHooks/useDashboardConfigSelector';
import useDashboardSchemaId from '../../../../utils/useDashboardSchemaId';
import usePermission from '../../../../../../utils/hooks/permissionHooks/usePermission';
import FilterWidget from '../../../../../../components/FilterWidget';
import usePopoverController from '../../../../../../components/Popover/usePopoverController';
import { assertCondition } from '../../../../../../utils/types';
import { UnreachableCaseError } from 'ts-essentials';
import produce from 'immer';
import { isNil } from 'lodash';
import KubitTooltip from '../../../../../../components/KubitTooltip';
import styled from 'styled-components';

const predicateContext: IPredicateContext = {
  fieldContext: FieldContext.Filter,
  events: [defaultEventInput],
  func: null,
};

const MAX_CONDITIONS = 1;
const MAX_PREDICATES = 1;

interface IProps {
  includeAnyValue: boolean;
}

const StyledTextButton = styled(TextButton)`
  align-self: center;
`;

const AddDashboardFilter = ({ includeAnyValue }: IProps) => {
  const { closePopover, isPopoverOpen, openPopover, popoverAnchor } =
    usePopoverController<HTMLButtonElement>();

  const { checkIsPermitted } = usePermission();
  const dispatch = useAppDispatch();

  const { deactivateHighlighting } = useHighlightingContext();

  const draftCustomization = useDashboardSelector('draftCustomization');
  const draftFilter = draftCustomization.filter;

  const dashboardLogicOps = useDashboardConfigSelector('config').logicOps;
  const schemaId = useDashboardSchemaId();

  const isPermittedToReadField = !isNil(schemaId)
    ? checkIsPermitted({
        requiredPermissionKeys: [PermissionKey.FieldRead],
        schemaId,
      })
    : false;

  const filterTooltipOption: ITooltipOption | undefined = useMemo(() => {
    if (isNil(schemaId)) {
      return {
        content: 'All reports must share same schema to use dashboard filters',
      };
    }

    if (!isPermittedToReadField) {
      return defaultPermissionTooltipOption;
    }

    if (!isNil(draftFilter)) {
      if (
        draftFilter.__typename === 'NamedFilter' &&
        draftFilter.conditions.length > 0
      ) {
        return {
          content: 'Only one condition is supported for named filters',
        };
      }

      const conditionsLength = draftFilter.conditions.length;
      if (conditionsLength >= MAX_DASHBOARD_FILTER_CONDITIONS) {
        return {
          content: 'Maximum number of conditions reached',
        };
      }
    }

    return {
      content: 'Add a custom dashboard filter.',
    };
  }, [schemaId, draftFilter, isPermittedToReadField]);

  const handleAddFilter = useCallback(() => {
    openPopover();
    deactivateHighlighting();
  }, [deactivateHighlighting, openPopover]);

  const handleSubmitFilter = (submittedFilter: IFilterFieldsFragment) => {
    if (submittedFilter.__typename === 'NamedFilter' || isNil(draftFilter)) {
      dispatch(
        dashboardCustomizationSetFilter({
          newFilter: submittedFilter,
        }),
      );
    } else if (submittedFilter.__typename === 'BasicFilter') {
      assertCondition(draftFilter.__typename === 'BasicFilter');
      const conditions = submittedFilter.conditions;
      assertCondition(conditions.length === 1);
      const { logicOps, predicates } = conditions[0];
      assertCondition(predicates.length === 1);
      const newFilter = produce(draftFilter, (draft) => {
        draft.conditions.push({
          __typename: 'Condition',
          predicates,
          logicOps,
        });
        if (draft.conditions.length > 1) {
          draft.logicOps.push(defaultLogicOp);
        }
      });
      dispatch(dashboardCustomizationSetFilter({ newFilter }));
    } else {
      throw new UnreachableCaseError(submittedFilter);
    }

    closePopover();
  };

  const canAddFilter = useMemo(() => {
    if (isNil(schemaId) || !isPermittedToReadField) {
      return false;
    }

    if (isNil(draftFilter)) {
      return true;
    }

    if (
      draftFilter.__typename === 'NamedFilter' &&
      draftFilter.conditions.length === 0
    ) {
      return true;
    }

    if (draftFilter.__typename === 'BasicFilter') {
      const conditionsLength = draftFilter.conditions.length;
      if (conditionsLength < MAX_DASHBOARD_FILTER_CONDITIONS) {
        return true;
      }
    }

    return false;
  }, [draftFilter, isPermittedToReadField, schemaId]);

  return (
    <>
      <KubitTooltip {...filterTooltipOption} placement="bottom">
        <StyledTextButton
          ref={popoverAnchor}
          onClick={handleAddFilter}
          disabled={!canAddFilter}
        >
          + Add Dashboard Filter
        </StyledTextButton>
      </KubitTooltip>

      <Popover
        isOpen={isPopoverOpen}
        close={closePopover}
        anchor={popoverAnchor.current}
      >
        {!isNil(schemaId) && (
          <FilterWidget
            onSubmit={handleSubmitFilter}
            schemaId={schemaId}
            context={predicateContext}
            defaultFilter={null}
            defaultLogOps={dashboardLogicOps}
            maxConditions={MAX_CONDITIONS}
            maxPredicates={MAX_PREDICATES}
            includeAnyValue={includeAnyValue}
            disableNamedFilters={
              draftFilter?.conditions && draftFilter.conditions.length > 0
            }
          />
        )}
      </Popover>
    </>
  );
};

export default AddDashboardFilter;
